import { useState } from 'react';
import { Check, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { PLANS, SIGNUP_URL } from '../data/mock';

export default function PricingTable({ compact = false }) {
  const [billing, setBilling] = useState('monthly');

  return (
    <section className="section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="chip mb-4">Transparent Pricing</p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900">
            Simple Pricing. Powerful Results.
          </h2>
          <p className="mt-4 text-slate-600">
            Choose the perfect plan for your business. Pay only for your ACTIWA plan and Meta's official conversation charges. No markups. No surprises.
          </p>

          {!compact && (
            <div className="mt-8 inline-flex bg-slate-100 rounded-full p-1">
              {['monthly', 'yearly'].map((b) => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors ${
                    billing === b ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {b === 'monthly' ? 'Monthly' : 'Yearly (Save 20%)'}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-3xl p-7 flex flex-col card-lift ${
                p.highlight
                  ? 'bg-slate-900 text-white border-2 border-brand-purple shadow-xl'
                  : 'bg-white text-slate-900 border border-slate-200'
              }`}
            >
              {p.tag && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-purple text-white text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Star size={12} className="fill-white" /> {p.tag}
                </span>
              )}
              <h3 className="text-lg font-bold">{p.name}</h3>
              <p className={`mt-1 text-xs ${p.highlight ? 'text-slate-300' : 'text-slate-500'}`}>
                {p.note}
              </p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">{p.price}</span>
                {p.period && (
                  <span className={`text-sm ${p.highlight ? 'text-slate-300' : 'text-slate-500'}`}>
                    {p.period}
                  </span>
                )}
              </div>
              {p.name === 'Enterprise' ? (
                <Link to="/contact" className="mt-5">
                  <Button className={`w-full rounded-full h-11 bg-slate-900 hover:bg-slate-800 text-white`}>
                    {p.cta}
                  </Button>
                </Link>
              ) : (
                <a href={SIGNUP_URL} target="_blank" rel="noreferrer" className="mt-5">
                  <Button
                    className={`w-full rounded-full h-11 ${
                      p.highlight
                        ? 'bg-brand-purple hover:bg-brand-purple-dark text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {p.cta}
                  </Button>
                </a>
              )}
              <ul className="mt-6 space-y-2.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check
                      size={16}
                      className={`mt-0.5 shrink-0 ${p.highlight ? 'text-brand-green' : 'text-brand-green-dark'}`}
                    />
                    <span className={p.highlight ? 'text-slate-100' : 'text-slate-700'}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          No setup fees • Cancel anytime • 18% GST extra as applicable
        </p>
      </div>
    </section>
  );
}
